export interface FunctionalityGroup {
  title: string;
  items: string[];
}

export interface ReleaseNote {
  version: string;
  date?: string;
  title: string;
  changes: string[];
}

export const FUNCTIONALITIES: FunctionalityGroup[] = [
  {
    title: 'Private connection',
    items: [
      'Encrypted, temporary Firebase signaling followed by direct WebRTC transfer',
      'Exactly two participants, host approval, revocable invitation links, QR codes, and native sharing',
      'No accounts, analytics, cloud file storage, relay server, advertising, or transfer-size quota',
      'Locally renameable trusted contacts, with matching verification phrases and deliberate rendezvous',
    ],
  },
  {
    title: 'Files and folders',
    items: [
      'Multiple files, recursive folders, empty files and directories, drag-and-drop, and transfers in either direction',
      'Host-side preparation while an invitation is waiting, with an automatic offer immediately after the direct handshake',
      'Prepared parcels for trusted contacts and one-time pre-authorized receiving',
      'Selective file acceptance, destination write testing, and explicit conflict handling',
      'Sequential verified blocks, bounded memory, pause, resume, cancel, retry, skip, queue ordering, speed, and ETA',
    ],
  },
  {
    title: 'Recovery and devices',
    items: [
      'Local checkpoints, prepared-parcel handles, unfinished-transfer recovery, and privacy-safe local history',
      'Installable PWA shell, Android support where destination-folder access is available, and mobile visibility protection',
      'Wake lock, completion alerts, connection quality, verified receipts, troubleshooting, and privacy-safe diagnostics',
    ],
  },
  {
    title: 'Passenger Lounge',
    items: [
      'Optional Ferry Four, Dock & Boxes, Signal Showdown, and tic-tac-toe over the direct peer connection',
      'Either person can propose a game; the other person explicitly votes yes or declines before play starts',
      'Deterministic local rule validation, accessible controls, rematches, and fair commit-reveal choices in Signal Showdown',
    ],
  },
];

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    version: '2.2.0',
    date: '2026-07-22',
    title: 'Ready before they arrive',
    changes: [
      'Hosts can select files and folders while waiting, review the selection, and queue an automatic offer for the approved recipient.',
      'The queued offer is published immediately after the direct capability handshake; the recipient still explicitly chooses a destination and accepts before bytes move.',
      'Added a waiting-period screen wake lock and safe fallbacks when the recipient is send-only or cannot receive folders.',
      'Fixed service-worker network failures so route-like requests fall back to the cached app shell and all other intercepted failures settle with an explicit offline response.',
      'A declined outgoing offer now leaves the queue cleanly instead of remaining displayed as waiting.',
    ],
  },
  {
    version: '2.1.1',
    date: '2026-07-20',
    title: 'Dock board lines',
    changes: [
      'Fixed the Dock & Boxes grid geometry so all horizontal and vertical edges render in their correct positions before and after selection.',
      'Kept unclaimed edges visible while waiting for the other person’s turn.',
    ],
  },
  {
    version: '2.1.0',
    date: '2026-07-20',
    title: 'Passenger Lounge',
    changes: [
      'Added four optional direct peer-to-peer games: Ferry Four (Connect Four), Dock & Boxes (Dots and Boxes), Signal Showdown (Rock Paper Scissors), and tic-tac-toe.',
      'Added a clear proposal and vote flow: either person can invite, while the other explicitly accepts or declines before a game begins.',
      'Kept game messages off Firebase and file payload traffic; older clients simply omit the optional lounge capability.',
      'Added accessible game controls, responsive phone layouts, leave/rematch actions, deterministic move validation, and cryptographic commit-reveal fairness for Signal Showdown.',
    ],
  },
  {
    version: '2.0.3',
    date: '2026-07-20',
    title: 'Contact names',
    changes: [
      'Added inline renaming for trusted contacts. Names remain local and do not alter the saved peer identity or verification phrase.',
    ],
  },
  {
    version: '2.0.2',
    date: '2026-07-20',
    title: 'Faster invitation joining',
    changes: [
      'Invitation links now focus the optional display-name field immediately and submit the connection request when Enter is pressed.',
    ],
  },
  {
    version: '2.0.1',
    date: '2026-07-19',
    title: 'Reliable transfer handshake',
    changes: [
      'Fixed a join timing race that could leave one browser waiting for transfer capabilities and show “The other browser did not finish the transfer handshake.”',
      'Added a two-peer regression test that deliberately drops the first capability hello and verifies both browsers recover.',
    ],
  },
  {
    version: '2.0.0',
    date: '2026-07-18',
    title: 'Trusted crossings',
    changes: [
      'Added mutually approved local trusted contacts with rotating rendezvous rooms and peer authentication.',
      'Added prepared parcels and one-time trusted receiving with destination permission preflight.',
      'Added local invitation QR codes and native device sharing.',
      'Added selective acceptance, queue ordering, destination tests, and conflict policies.',
      'Added privacy-safe local transfer history and an optional direct-path speed test.',
      'Added maintained in-app functionality and release-note sections.',
    ],
  },
  {
    version: '1.4.4',
    title: 'Transfer polish',
    changes: ['Added connection quality, completion alerts, selection review, revocable invitations, verified receipts, and expanded device guidance.'],
  },
  {
    version: '1.4.3',
    title: 'Safe PWA updates',
    changes: ['Added controlled service-worker updates without reloading active rooms or transfers.'],
  },
  {
    version: '1.4.2',
    title: 'Signaling reliability',
    changes: ['Moved signaling timestamps to Firebase server timestamps to prevent clock-skew permission failures.'],
  },
  {
    version: '1.4.1',
    title: 'App Check compatibility',
    changes: ['Expanded the production security policy for reCAPTCHA Enterprise and documented configuration diagnostics.'],
  },
  {
    version: '1.3.0',
    title: 'PairFerry identity',
    changes: ['Introduced the PairFerry name, installable PWA experience, mobile capability modes, and updated local recovery namespace.'],
  },
  {
    version: '1.0.0',
    title: 'First direct crossing',
    changes: ['Shipped encrypted two-person signaling and direct, verified multiple-file and folder transfer.'],
  },
];
